// @flow

import * as React from 'react';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';

// Components
import Button from 'components/atoms/Button';

// Constants
import { APP_BREAKPOINTS } from 'constants/appConfig';

import './DrawingComponent.scss';

const defaultBase64 =
  'data:image/gif;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

const { MD } = APP_BREAKPOINTS;

export type Props = {
  intl: IntlShape,
  setSignature: (?string) => void,
  setSelfCheckInSigned: string => void,
  close: () => void,
  signature?: string,
  buttonRight: Function,
  fullScreen?: boolean
};

type State = {
  lineWidth: number,
  penColor: string,
  pen: string,
  penCoords: number[],
  touchX: number,
  touchY: number,
  typeTouch: string,
  isReset: boolean
};

class DrawingComponent extends React.Component<Props, State> {
  static block = 'drawing-component';

  constructor(props: Props) {
    super(props);
    this.state = {
      mouseDown: 0,
      lineWidth: 0,
      pen: 'down',
      penColor: '',
      penCoords: [0, 0],
      touchX: 0,
      touchY: 0,
      typeTouch: '',
      isReset: !this.props.signature
    };
    (this: any).load = this.load.bind(this);
    (this: any).resize = this.resize.bind(this);
    (this: any).canvas = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('resize', this.resize);
    this.load();
  }

  componentDidUpdate() {
    this.elem.addEventListener('touchstart', e => {
      e.preventDefault();
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
  }

  canvas: React.Ref<any>;
  elem: any;
  ctx: any;

  getWidthHeight = () => {
    const { fullScreen } = this.props;
    if (window.innerWidth <= MD) {
      // MD max width in mobile
      let windowWidth, windowHeight;
      const maxWidth = 434;
      if (window.innerWidth < maxWidth) {
        windowWidth = window.innerWidth - 64; // margin left-right 64 in portrait
        windowHeight = windowWidth / 1.9; // 1.9 ratio
      } else {
        if (fullScreen) {
          windowWidth = window.innerWidth - window.innerWidth * 0.3; // margin left-right 0.30 in landscape
          windowHeight = windowWidth / 2.4; // 2.4 ratio
        } else {
          windowWidth = maxWidth - maxWidth * 0.15; // margin left-right 0.15 in landscape
          windowHeight = windowWidth / 1.9; // 1.9 ratio
        }
      }
      return [windowWidth, windowHeight];
    } else {
      // Signature field size for desktop
      return [352, 352 / 1.9];
    }
  };

  resize() {
    const { signature } = this.props;
    const img = new Image();

    if (signature) {
      img.src = 'data:image/png;base64,' + signature;
    } else {
      img.src = defaultBase64;
    }

    img.onload = () => {
      const [canvasWidth, canvasHeight] = this.getWidthHeight();
      this.refs.canvas.width = canvasWidth;
      this.refs.canvas.height = canvasHeight;
      this.ctx.drawImage(
        img,
        0,
        0,
        this.refs.canvas.width,
        this.refs.canvas.height
      );
      if (img.src !== defaultBase64) {
        this.getBase64();
      }
    };
  }

  drawDot(ctx: any, x: number, y: number) {
    ctx.fillStyle = 'black';
    ctx.imageSmoothingEnabled = false;
    if (this.state.typeTouch === 'touchStart') {
      ctx.beginPath();
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  }

  drawing = (e: any) => {
    e.preventDefault();
    if (this.state.pen === 'down') {
      this.ctx.beginPath();
      this.ctx.lineWidth = this.state.lineWidth;
      this.ctx.lineCap = 'round';
      this.ctx.strokeStyle = this.state.penColor;
      this.ctx.moveTo(this.state.penCoords[0], this.state.penCoords[1]);
      this.ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      this.ctx.stroke();
      this.setState({
        penCoords: [e.nativeEvent.offsetX, e.nativeEvent.offsetY]
      });
      this.getBase64();
    }
  };

  outSideCanvas = (e: SyntheticInputEvent<HTMLInputElement>) => {
    this.penUp(e);
  };

  penDown = (e: any) => {
    this.setState({
      pen: 'down',
      penCoords: [e.nativeEvent.offsetX, e.nativeEvent.offsetY],
      isReset: false
    });
  };

  penUp = (e: SyntheticInputEvent<HTMLInputElement>) => {
    this.setState({
      pen: 'up'
    });
  };

  getBase64 = () => {
    const { setSignature, setSelfCheckInSigned } = this.props;
    let imageBase64 = this.refs.canvas.toDataURL('image/png');
    imageBase64 = imageBase64.replace('data:image/png;base64,', '');
    setSignature(imageBase64);
    setSelfCheckInSigned('Y');
  };

  touchStart = (e: any) => {
    this.getTouchPos(e);
    this.drawDot(this.ctx, this.state.touchX, this.state.touchY);
    this.setState({
      typeTouch: 'touchStart',
      isReset: false
    });
    e.preventDefault();
  };

  touchEnd = (e: any) => {
    this.setState({
      typeTouch: 'touchEnd'
    });
    this.ctx.beginPath();
    e.preventDefault();
  };

  touchMove = (e: any) => {
    this.getTouchPos(e);
    this.drawDot(this.ctx, this.state.touchX, this.state.touchY);
    this.setState({
      typeTouch: 'touchMove'
    });
    this.getBase64();
  };

  getTouchPos = (e: any) => {
    if (e && e.touches) {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        this.setState({
          touchX: touch.pageX - touch.target.offsetLeft,
          touchY: touch.pageY - touch.target.offsetTop
        });
      }
    }
  };

  load() {
    const { lineWidth } = this.state;
    const { signature } = this.props;

    this.setState({
      pen: 'up',
      lineWidth: 5,
      penColor: 'black'
    });
    this.ctx = this.refs.canvas.getContext('2d');
    this.ctx.lineWidth = lineWidth;
    const img = new Image();
    if (signature) {
      img.src = 'data:image/png;base64,' + signature;
    } else {
      img.src = defaultBase64;
    }

    img.onload = () => {
      const [canvasWidth, canvasHeight] = this.getWidthHeight();
      this.refs.canvas.width = canvasWidth;
      this.refs.canvas.height = canvasHeight;
      this.ctx.drawImage(
        img,
        0,
        0,
        this.refs.canvas.width,
        this.refs.canvas.height
      );
    };
  }

  reset() {
    const { setSignature, setSelfCheckInSigned } = this.props;
    this.setState(
      {
        isReset: true
      },
      () => this.load()
    );
    setSignature(null);
    setSelfCheckInSigned('N');
  }

  render() {
    const ns = DrawingComponent.block;
    const { intl, buttonRight, signature } = this.props;
    const { isReset } = this.state;
    const clearBtnClassName = classNames({
      'my-1': true,
      [`${ns}__clear-btn`]: signature,
      [`${ns}__clear-btn--disabled`]: !signature
    });
    return (
      <div className={ns}>
        <div className={`d-flex justify-content-center align-items-center`}>
          {isReset && (
            <div className={`${ns}__sign-label`}>
              {intl.formatMessage({
                id: 'c8.self.check.in.signature.sign.here'
              })}
            </div>
          )}
          <div className={`${ns}__canvas`} ref={elem => (this.elem = elem)}>
            <canvas
              ref="canvas"
              onMouseMove={e => this.drawing(e)}
              onMouseDown={e => this.penDown(e)}
              onMouseLeave={e => this.outSideCanvas(e)}
              onMouseUp={e => this.penUp(e)}
              onTouchMove={e => this.touchMove(e)}
              onTouchStart={e => this.touchStart(e)}
              onTouchEnd={e => this.touchEnd(e)}
            />
          </div>
        </div>
        <div>
          <Button
            className={clearBtnClassName}
            buttonTheme="link"
            onClick={() => this.reset()}
            disabled={!signature}
          >
            {intl.formatMessage({
              id: 'c8.self.check.in.signature.clear'
            })}
          </Button>
          {buttonRight && (
            <div className={`${ns}__button-right`}>{buttonRight()}</div>
          )}
        </div>
      </div>
    );
  }
}

export default injectIntl(DrawingComponent);



// WEBPACK FOOTER //
// ./src/components/atoms/DrawingComponent/DrawingComponent.js